/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LocaleLink } from "@/components/LocaleLink";
import { AuroraBackground } from "@/components/AuroraBackground";
import { AuroraHeart } from "@/components/AuroraHeart";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { AppVideo } from "@/components/AppVideo";
import { Heart, BookOpen, Sparkles, Compass, ChevronRight } from "lucide-react";

const FaithPage = () => {
  const { t, i18n } = useTranslation(["site", "landing"]);
  // Localized hero video: Portuguese gets the PT recording, everything else
  // falls back to the English one. (Rename convention: <LANG>_<clip>.)
  const lang = (i18n.language || "en").slice(0, 2).toLowerCase();
  const faithClip = lang === "pt" ? "PT_faith-prayer-tour" : "EN_faith-prayer-tour";
  const tk = (k, fb) => t(`site:faith.${k}`, fb);
  return (
    <>
      <section className="relative pb-16">
        <AuroraBackground variant="gold" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-8 lg:pt-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="aurora-chip mb-7">
                <span className="size-1.5 rounded-full bg-[var(--aurora-gold)]" />
                {tk("kicker")}
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[4rem] leading-[1.05] tracking-tight font-light">
                {tk("title1")}{" "}
                <em className="italic aurora-shimmer-text">{tk("title2")}</em>
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 text-white/75 text-lg leading-relaxed font-light max-w-xl">
                {tk("subtitle")}
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-8">
                <StoreBadgeGroup startLabel={t("site:common.startFree")} />
                <p className="mt-3 text-xs text-white/45 tracking-wide">{t("site:home.freeBadge")}</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={180}>
              <div className="phone-frame phone-frame--device max-w-[330px] mx-auto animate-float-slow">
                <AppVideo
                  className="phone-screen"
                  island
                  sources={[
                    { src: `/assets/media/clips/${faithClip}.mp4`, type: "video/mp4" },
                  ]}
                  poster={`/assets/media/clips/${faithClip}.jpg`}
                  label={tk("phoneAlt", "Faith-aware prayer and multi-tradition comparison in the Aurora app")}
                  soundLabel={t("landing:hero.preview.soundLabel", "Tap for sound")}
                  rounded="49px"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow">{tk("modesEyebrow")}</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light max-w-2xl">
              {tk("modesTitle")}
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { tag: tk("secularTag"), title: tk("secularTitle"), body: tk("secularBody"), bg: "var(--aurora-analytics)", icon: <Compass size={20} /> },
              { tag: tk("awareTag"), title: tk("awareTitle"), body: tk("awareBody"), bg: "var(--aurora-journeys)", icon: <Sparkles size={20} /> },
              { tag: tk("forwardTag"), title: tk("forwardTitle"), body: tk("forwardBody"), bg: "var(--aurora-emotional)", icon: <BookOpen size={20} /> },
            ].map((c) => (
              <Reveal key={c.tag}>
                <div className="aurora-card p-7 h-full" style={{ background: c.bg }}>
                  <span className="size-11 rounded-2xl bg-white/8 flex items-center justify-center text-[var(--aurora-gold)] mb-5">
                    {c.icon}
                  </span>
                  <p className="text-[0.7rem] uppercase tracking-[0.25em] text-[var(--aurora-cyan)] mb-2">
                    {c.tag}
                  </p>
                  <h3 className="font-serif text-2xl">{c.title}</h3>
                  <p className="mt-3 text-white/72 font-light leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow">{tk("prayerEyebrow")}</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight font-light">
              {tk("prayerTitle1")} <em className="italic text-[var(--aurora-gold)]">{tk("prayerTitle2")}</em>
            </h2>
            <p className="mt-4 text-white/72 font-light text-lg leading-relaxed max-w-lg">
              {tk("prayerBody")}
            </p>
            <blockquote className="mt-7 border-l-2 border-[var(--aurora-gold)]/60 pl-5 font-serif italic text-white/85 text-xl leading-relaxed max-w-md">
              "{tk("prayerQuote")}"
              <p className="mt-3 text-sm not-italic text-white/45 tracking-wide">— {tk("prayerAttribution")}</p>
            </blockquote>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <img
              src="/assets/gallery/faith-sample-prayer-ios.png"
              alt="Sample prayer generated in Aurora"
              className="rounded-3xl border border-white/8 shadow-2xl mx-auto max-w-[340px]"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <Heart className="mx-auto text-[var(--aurora-gold)]" size={28} />
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight font-light leading-[1.1]">
              {tk("closingTitle1")}{" "}
              <em className="italic aurora-shimmer-text">{tk("closingTitle2")}</em>
            </h2>
            <p className="mt-6 text-white/75 text-lg font-light max-w-xl mx-auto leading-relaxed">
              {tk("closingBody")}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup
                startLabel={tk("closingCta")}
                appleTestId="faith-download-apple"
                googleTestId="faith-download-google"
              />
            </div>
            <p className="mt-4 text-xs text-white/45 tracking-wide">{t("site:common.freeFooter")}</p>
            <p className="mt-8">
              <LocaleLink to="/christian" className="text-[var(--aurora-cyan)] inline-flex items-center gap-1.5 hover:gap-2 transition-all">
                {tk("closingChristianLink")} <ChevronRight size={14} />
              </LocaleLink>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default FaithPage;
